import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { Input } from '@/components/ui/field';
import { brParaISO, isoParaBR } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * CampoData — dd/mm/aaaa digitável + calendário visual no mesmo campo.
 *
 * `<input type="date">` puro delega a formatação ao locale do navegador/SO, o
 * que não garante pt-BR (Playbook: nunca depender de locale implícito para uma
 * regra de negócio). Aqui o valor exibido/digitado é sempre dd/mm/aaaa; o
 * calendário é só um atalho visual para o mesmo campo de texto — o valor
 * canônico continua ISO (yyyy-mm-dd), igual ao resto do app.
 */

const DIAS_SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

function primeiroDoMes(iso: string): Date {
  const [ano, mes] = iso.split('-').map(Number);
  return new Date(ano, (mes ?? 1) - 1, 1);
}

function isoDeAncora(ancora: Date, dia: number): string {
  const ano = ancora.getFullYear();
  const mes = ancora.getMonth();
  return `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

function ehFimDeSemana(iso: string): boolean {
  const [ano, mes, dia] = iso.split('-').map(Number);
  const diaSemana = new Date(ano, mes - 1, dia).getDay();
  return diaSemana === 0 || diaSemana === 6;
}

export function CampoData({
  id,
  valor,
  aoMudar,
  min,
  max,
  bloquearFinsDeSemana,
  disabled,
  'aria-describedby': describedBy,
  'aria-invalid': ariaInvalid,
}: {
  id: string;
  /** ISO civil (yyyy-mm-dd). */
  valor: string;
  aoMudar: (iso: string) => void;
  /** ISO civil — datas antes desta ficam desabilitadas no calendário e são rejeitadas na digitação. */
  min?: string;
  /** ISO civil — datas depois desta ficam desabilitadas no calendário e são rejeitadas na digitação. */
  max?: string;
  /** Sábado/domingo ficam desabilitados no calendário e são rejeitados na digitação. */
  bloquearFinsDeSemana?: boolean;
  disabled?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}) {
  const [aberto, setAberto] = React.useState(false);
  const [texto, setTexto] = React.useState(() => isoParaBR(valor));
  const [ancora, setAncora] = React.useState(() => primeiroDoMes(valor || min || max || new Date().toISOString()));

  React.useEffect(() => {
    setTexto(isoParaBR(valor));
  }, [valor]);

  function commitar(bruto: string) {
    const iso = brParaISO(bruto);
    const valido =
      iso && (!min || iso >= min) && (!max || iso <= max) && (!bloquearFinsDeSemana || !ehFimDeSemana(iso));
    if (valido) {
      aoMudar(iso);
      setTexto(isoParaBR(iso));
    } else {
      setTexto(isoParaBR(valor));
    }
  }

  function escolherDia(iso: string) {
    aoMudar(iso);
    setTexto(isoParaBR(iso));
    setAberto(false);
  }

  const diasNoMes = new Date(ancora.getFullYear(), ancora.getMonth() + 1, 0).getDate();
  const offsetSemana = new Date(ancora.getFullYear(), ancora.getMonth(), 1).getDay();
  const celulas: (number | null)[] = [
    ...Array.from({ length: offsetSemana }, () => null),
    ...Array.from({ length: diasNoMes }, (_, indice) => indice + 1),
  ];

  return (
    <PopoverPrimitive.Root
      open={aberto}
      onOpenChange={(novoAberto) => {
        setAberto(novoAberto);
        if (novoAberto) setAncora(primeiroDoMes(valor || min || max || new Date().toISOString()));
      }}
    >
      <div className="relative">
        <Input
          id={id}
          value={texto}
          disabled={disabled}
          placeholder="dd/mm/aaaa"
          inputMode="numeric"
          aria-describedby={describedBy}
          aria-invalid={ariaInvalid}
          className="pr-10"
          onChange={(evento) => setTexto(evento.target.value)}
          onBlur={(evento) => commitar(evento.target.value)}
          onKeyDown={(evento) => {
            if (evento.key === 'Enter') {
              evento.preventDefault();
              commitar((evento.target as HTMLInputElement).value);
            }
          }}
        />
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="Abrir calendário"
            className="absolute right-1 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-chip text-icon transition-colors duration-fast ease-standard hover:bg-bg-subtle disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CalendarDays className="size-4" aria-hidden="true" />
          </button>
        </PopoverPrimitive.Trigger>
      </div>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          // z-[70]: acima de diálogos e folhas (o popover renderiza num portal próprio).
          className="z-[70] w-72 rounded-input border border-border bg-bg-surface p-3 shadow-md duration-base ease-standard data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              aria-label="Mês anterior"
              onClick={() => setAncora(new Date(ancora.getFullYear(), ancora.getMonth() - 1, 1))}
              className="flex size-7 items-center justify-center rounded-chip text-icon transition-colors duration-fast ease-standard hover:bg-bg-subtle"
            >
              <ChevronLeft className="size-4" aria-hidden="true" />
            </button>
            <span className="text-label font-bold text-on-surface">
              {MESES[ancora.getMonth()]} {ancora.getFullYear()}
            </span>
            <button
              type="button"
              aria-label="Próximo mês"
              onClick={() => setAncora(new Date(ancora.getFullYear(), ancora.getMonth() + 1, 1))}
              className="flex size-7 items-center justify-center rounded-chip text-icon transition-colors duration-fast ease-standard hover:bg-bg-subtle"
            >
              <ChevronRight className="size-4" aria-hidden="true" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {DIAS_SEMANA.map((rotulo, indice) => (
              <span key={`${rotulo}-${indice}`} className="text-micro font-normal uppercase text-on-surface-muted">
                {rotulo}
              </span>
            ))}
            {celulas.map((dia, indice) => {
              if (dia === null) return <span key={`vazio-${indice}`} />;
              const iso = isoDeAncora(ancora, dia);
              const desabilitado = Boolean(
                (min && iso < min) || (max && iso > max) || (bloquearFinsDeSemana && ehFimDeSemana(iso)),
              );
              const selecionado = iso === valor;
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={desabilitado}
                  aria-current={selecionado ? 'date' : undefined}
                  onClick={() => escolherDia(iso)}
                  className={cn(
                    'flex size-8 items-center justify-center rounded-chip text-body transition-colors duration-fast ease-standard',
                    desabilitado && 'cursor-not-allowed text-on-surface-muted opacity-40',
                    !desabilitado && !selecionado && 'hover:bg-bg-subtle',
                    selecionado && 'bg-primary font-bold text-on-primary',
                  )}
                >
                  {dia}
                </button>
              );
            })}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
